/**
 * @module core/gsap-setup
 *
 * Single place where GSAP is configured. Every other module imports `gsap` and
 * the plugins from here, which guarantees:
 *
 *  1. Plugins are registered exactly once, before first use.
 *  2. Project-wide defaults (ease, duration) are consistent.
 *  3. `prefers-reduced-motion` is honoured globally via `gsap.matchMedia()`.
 *
 * Since Webflow's acquisition of GSAP, every plugin — including SplitText,
 * DrawSVG and MorphSVG — ships free in the public `gsap` npm package, so no
 * auth token or private registry is required.
 */

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollSmoother } from "gsap/ScrollSmoother";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { DrawSVGPlugin } from "gsap/DrawSVGPlugin";
import { MorphSVGPlugin } from "gsap/MorphSVGPlugin";
import { Observer } from "gsap/Observer";
import { Flip } from "gsap/Flip";
import { SplitText } from "gsap/SplitText";

export {
  gsap,
  ScrollTrigger,
  ScrollSmoother,
  ScrollToPlugin,
  DrawSVGPlugin,
  MorphSVGPlugin,
  Observer,
  Flip,
  SplitText,
};

/**
 * Shared easing vocabulary. Using named constants keeps the motion language
 * consistent — important for the "one continuous experience" feel.
 */
export const EASE = {
  /** Standard UI entrance. */
  out: "power3.out",
  /** Symmetrical in/out for scrubbed motion. */
  inOut: "power2.inOut",
  /** Snappy, decisive. */
  snap: "power4.out",
  /** Gentle overshoot for interactive feedback. */
  overshoot: "back.out(1.6)",
  /** Soft, floaty for ambient loops. */
  float: "sine.inOut",
  /** Linear for continuous rotation. */
  linear: "none",
};

/** Motion durations in seconds, tuned to feel cohesive. */
export const DURATION = {
  fast: 0.35,
  base: 0.7,
  slow: 1.1,
  ambient: 18,
};

let registered = false;

/**
 * Register plugins and apply global defaults. Idempotent.
 *
 * @returns {void}
 */
export function registerPlugins() {
  if (registered) return;
  registered = true;

  const plugins = [ScrollTrigger, ScrollToPlugin, DrawSVGPlugin, MorphSVGPlugin, Observer, Flip, ScrollSmoother];
  if (SplitText) plugins.push(SplitText);

  gsap.registerPlugin(...plugins);

  gsap.defaults({
    duration: DURATION.base,
    ease: EASE.out,
    overwrite: "auto",
  });

  // Avoid GSAP's built-in "lag smoothing" jumping animations after a heavy
  // frame (common on low-end devices during long scrolls).
  gsap.ticker.lagSmoothing(500, 33);
}

/**
 * Create a scoped `gsap.matchMedia()` instance.
 *
 * All animations should be created inside a `matchMedia` block so that:
 *  - `prefers-reduced-motion` users get a static, accessible page;
 *  - responsive breakpoints get different motion treatments;
 *  - `context.revert()` removes *everything* created inside it (no leaks).
 *
 * @returns {import("gsap").MatchMedia} A fresh matchMedia instance.
 */
export function createMotionContext() {
  return gsap.matchMedia();
}

/**
 * Build the shared media-query conditions.
 *
 * `ensureInitialState` is the reduced-motion branch: elements that would
 * normally be hidden before animating are made visible immediately.
 */
export const MEDIA = {
  /** Full, expressive motion — pointer devices with room to breathe. */
  full: "(prefers-reduced-motion: no-preference) and (min-width: 900px)",
  /** Room for the large orbital scenes. */
  wide: "(prefers-reduced-motion: no-preference) and (min-width: 1080px)",
  /** Reduced motion (or very small screens): no scroll choreography. */
  reduce: "(prefers-reduced-motion: reduce)",
  /** Any no-preference device, mobile included. */
  any: "(prefers-reduced-motion: no-preference)",
  /** Coarse pointer => skip hover-only affordances. */
  mobile: "(max-width: 899px)",
};

/**
 * Split text into lines/words/chars with a safe fallback.
 *
 * @param {string|Element|Element[]} target
 * @param {Object} [vars] SplitText configuration.
 * @returns {{lines?: Element[], words?: Element[], chars?: Element[], revert: () => void}}
 *   A SplitText-like object. When splitting is unavailable or fails, a stub
 *   with a no-op `revert()` is returned so callers never crash.
 */
export function splitText(target, vars = {}) {
  const config = {
    type: "lines,words",
    linesClass: "split-line",
    wordsClass: "split-word",
    // SplitText writes an `aria-label` on the parent and hides the generated
    // fragments from assistive technology — critical for accessibility.
    aria: "auto",
    ...vars,
  };

  if (!SplitText) return { revert: () => {} };

  try {
    if (typeof SplitText.create === "function") {
      return SplitText.create(target, config);
    }
    return new SplitText(target, config);
  } catch (error) {
    console.warn("[gsap-setup] splitText failed; continuing unsplit.", error);
    return { revert: () => {} };
  }
}

/**
 * Utility: kill every ScrollTrigger instance. Mostly used by tests and by the
 * teardown path so no stale triggers survive a re-initialisation.
 *
 * @returns {void}
 */
export function killAllScrollTriggers() {
  ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
}
